import * as React from 'react'
import { addYears, format, setYear } from 'date-fns'
import { DayPicker, getDefaultClassNames, useDayPicker } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Icon } from '@/common/components/Icon'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: 'primary' | 'secondary' | 'tertiary'
  captionLayout?: 'label' | 'dropdown'
}

const YEARS_BEFORE = 100
const YEARS_AFTER = 10

function useYearOptions() {
  const context = useDayPicker()
  const dayPickerProps = context?.dayPickerProps

  return React.useMemo(() => {
    const currentYear = new Date().getFullYear()
    const startYear =
      dayPickerProps?.startMonth?.getFullYear() ?? currentYear - YEARS_BEFORE
    const endYear =
      dayPickerProps?.endMonth?.getFullYear() ?? currentYear + YEARS_AFTER

    return Array.from(
      { length: Math.max(endYear - startYear + 1, 1) },
      (_, i) => String(endYear - i),
    )
  }, [dayPickerProps?.startMonth, dayPickerProps?.endMonth])
}

function CalendarMonthCaption({
  calendarMonth,
  className,
  displayIndex,
  ...props
}: {
  calendarMonth: { date: Date }
  className?: string
  displayIndex?: number
} & React.HTMLAttributes<HTMLDivElement>) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker()
  const yearOptions = useYearOptions()
  const monthDate = calendarMonth.date
  const previousYear = addYears(monthDate, -1)
  const nextYear = addYears(monthDate, 1)

  return (
    <div
      className={cn(
        'flex items-center !justify-between gap-2 w-full border-b border-monochrome-120 pb-3 mb-3 mt-0',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-7 w-7 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (previousMonth) {
              goToMonth(previousYear)
            }
          }}
          disabled={!previousMonth}
          aria-label="Go to previous year"
        >
          <Icon
            icon="double-chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            className="rotate-180"
            fill="none"
          />
        </button>
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-7 w-7 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (previousMonth) {
              goToMonth(previousMonth)
            }
          }}
          disabled={!previousMonth}
          aria-label="Go to previous month"
        >
          <Icon
            icon="chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-white">
          {format(monthDate, 'MMM')}
        </span>
        <Select
          value={String(monthDate.getFullYear())}
          onValueChange={(year) => goToMonth(setYear(monthDate, Number(year)))}
        >
          <SelectTrigger
            className="h-7 w-auto gap-2 rounded-full border border-monochrome-120 bg-monochrome-3-60 px-2.5 py-0 text-sm font-semibold text-white"
            aria-label="Select year"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[260px]">
            {yearOptions.map((year) => (
              <SelectItem key={year} value={year} className="py-2">
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-7 w-7 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (nextMonth) {
              goToMonth(nextMonth)
            }
          }}
          disabled={!nextMonth}
          aria-label="Go to next month"
        >
          <Icon
            icon="chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="rotate-180"
          />
        </button>
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-7 w-7 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (nextMonth) {
              goToMonth(nextYear)
            }
          }}
          disabled={!nextMonth}
          aria-label="Go to next year"
        >
          <Icon
            icon="double-chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          />
        </button>
      </div>
    </div>
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  buttonVariant = 'primary',
  captionLayout = 'label',
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      className={cn('p-2.5 flex flex-col overflow-hidden', className)}
      classNames={{
        root: cn('w-full flex flex-col', defaultClassNames.root),
        months: cn(
          'flex gap-4 flex-col md:flex-row relative',
          defaultClassNames.months,
        ),
        month: cn('flex flex-col w-full', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          'absolute bg-popover inset-0 opacity-0',
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : 'rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        month_grid: cn(
          'w-full border-collapse !flex flex-col',
          defaultClassNames.month_grid,
        ),
        weekdays: cn('flex w-full mb-2', defaultClassNames.weekdays),
        weekday: cn(
          'text-body-medium text-monochrome-1 rounded-md flex-1 font-normal text-[0.8rem] select-none',
          defaultClassNames.weekday,
        ),
        weeks: cn('!flex flex-col gap-0.5', defaultClassNames.weeks),
        week: cn(
          'flex w-full h-[34px] shrink-0 items-center',
          defaultClassNames.week,
        ),
        week_number_header: cn(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-[0.8rem] select-none text-muted-foreground',
          defaultClassNames.week_number,
        ),
        day: cn(
          'relative [&_button]:text-monochrome-1 flex-1 p-0 text-body-medium text-center group/day select-none data-[selected=true]:bg-accent-1 data-[selected=true]:text-white data-[selected=true]:hover:bg-accent-1 rounded-full flex items-center justify-center',
          defaultClassNames.day,
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          'rounded-[10px] py-1 px-2 w-full h-full',
          'data-[selected=true]:bg-accent-1 data-[selected=true]:text-white data-[selected=true]:hover:bg-accent-1',
        ),
        range_start: cn(
          'rounded-l-md bg-accent',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: cn(
          'border border-monochrome-120 text-white rounded-full',
          defaultClassNames.today,
        ),
        outside: cn(
          'aria-selected:text-muted-foreground [&_button]:text-monochrome-2 [&_button]:hover:text-monochrome-1',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Nav: () => <div className="hidden" />,
        MonthCaption: CalendarMonthCaption,
        Chevron: ({ orientation, ...iconProps }) =>
          orientation === 'right' ? (
            <Icon
              icon="chevron-down"
              width="8"
              height="16"
              viewBox="0 0 8 16"
              {...iconProps}
            />
          ) : (
            <Icon
              icon="chevron-down"
              width="8"
              height="16"
              viewBox="0 0 8 16"
              {...iconProps}
            />
          ),
      }}
      formatters={{
        formatWeekdayName: (date) => format(date, 'EEE'),
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
